import { Link } from "react-router-dom";

interface SectionFinalCTAProps {
  headline?: string;
  subheadline?: string;
  ctaLabel?: string;
}

export default function SectionFinalCTA({
  headline = "Get the Legal Guidance You Need Today",
  subheadline = "Whether you're facing a compliance issue, contract dispute, or regulatory inquiry — our team is ready to provide the strategic legal counsel you need to protect your interests.",
  ctaLabel = "Schedule Your Consultation",
}: SectionFinalCTAProps) {
  return (
    <section className="relative overflow-hidden">
      <div className="grid lg:grid-cols-5 min-h-[420px]">
        {/* Left — navy content */}
        <div className="lg:col-span-3 bg-[#0A1628] px-10 lg:px-16 py-20 relative overflow-hidden flex items-center">
          {/* Decorative rings */}
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full border border-[#C9A961]/8" />
          <div className="absolute -top-12 -left-12 w-64 h-64 rounded-full border border-[#C9A961]/8" />
          <div className="absolute bottom-0 right-0 w-48 h-48 rounded-full border border-[#C9A961]/8" />

          <div className="relative z-10">
            <span className="block text-[11px] tracking-[0.25em] text-[#C9A961] uppercase font-inter font-medium mb-4">
              Take the Next Step
            </span>
            <h2 className="text-4xl lg:text-5xl font-playfair font-bold text-white leading-tight mb-5">
              {headline.includes("Today") ? (
                <>
                  {headline.split("Today")[0]}
                  <span className="italic text-[#C9A961]">Today</span>
                </>
              ) : (
                headline
              )}
            </h2>
            <p className="text-slate-300 font-inter text-base leading-relaxed mb-8 max-w-lg">
              {subheadline}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-5">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-[#C9A961] hover:bg-[#b8954f] text-[#0A1628] font-inter font-semibold px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {ctaLabel}
                <i className="ri-arrow-right-line" />
              </Link>
              <a
                href="tel:+12157352384"
                className="inline-flex items-center justify-center gap-2 border border-white/30 hover:border-[#C9A961] text-white hover:text-[#C9A961] font-inter font-medium px-8 py-4 rounded-md transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line" />
                (215) 735-2384
              </a>
            </div>
            <p className="text-slate-400 text-xs font-inter">
              <i className="ri-check-line text-[#C9A961] mr-1.5" />
              Free initial consultation &mdash; confidential and no-obligation
            </p>
          </div>
        </div>

        {/* Right — contact info card */}
        <div
          className="lg:col-span-2 relative flex items-center justify-center p-8 lg:p-10"
          style={{
            backgroundImage: `url("https://public.readdy.ai/ai/img_res/3eec2bb8bb6a276f431882525c0cbf3a.jpg")`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="absolute inset-0 bg-[#0A1628]/75" />
          <div className="relative z-10 bg-white rounded-2xl p-7 w-full max-w-xs">
            <div className="flex items-center gap-2 mb-5">
              <span className="w-8 h-8 flex items-center justify-center rounded-full bg-[#0A1628]">
                <i className="ri-building-4-line text-[#C9A961] text-sm" />
              </span>
              <h3 className="font-playfair font-bold text-[#0A1628] text-base">Contact Our Office</h3>
            </div>
            <div className="flex flex-col gap-4">
              {[
                {
                  icon: "ri-phone-line",
                  label: "Phone",
                  content: "(215) 735-2384",
                  href: "tel:+12157352384",
                },
                {
                  icon: "ri-mail-line",
                  label: "Email",
                  content: "info@gosfield.com",
                  href: "mailto:info@gosfield.com",
                },
                {
                  icon: "ri-time-line",
                  label: "Hours",
                  content: "Mon–Fri, 9 AM – 5:30 PM",
                  href: null,
                },
                {
                  icon: "ri-map-pin-2-line",
                  label: "Address",
                  content: "2309 Delancey Pl., Philadelphia, PA 19103",
                  href: null,
                },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#0A1628] flex-shrink-0">
                    <i className={`${item.icon} text-[#C9A961] text-sm`} />
                  </span>
                  <div>
                    <div className="text-[10px] text-slate-400 font-inter">{item.label}</div>
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-xs font-inter font-semibold text-[#0A1628] hover:text-[#C9A961] transition-colors cursor-pointer"
                      >
                        {item.content}
                      </a>
                    ) : (
                      <div className="text-xs font-inter font-semibold text-[#0A1628]">
                        {item.content}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
